import { useState, useEffect, useCallback, useRef } from "react";
import {
  Upload, Loader2, Landmark, CheckCircle2, Circle, EyeOff,
  Link2, Link2Off, Plus, Trash2, Sparkles, ArrowRight, FileSpreadsheet, Brain, Zap,
} from "lucide-react";
import { toast } from "sonner";
import { Card, CardContent } from "../ui/card";
import { Button } from "../ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "../ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../ui/alert-dialog";
import BankRulesDrawer from "./BankRulesDrawer";
import api from "../../lib/api";

const fmtEUR = (n) => new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR" }).format(n || 0);
const fmtDate = (iso) => { try { return new Date(iso).toLocaleDateString("es-ES"); } catch { return iso; } };

const STATUS_META = {
  unmatched: { label: "Sin emparejar", tone: "bg-amber-50 text-amber-800 border-amber-200", Icon: Circle },
  matched:   { label: "Conciliada",     tone: "bg-emerald-50 text-emerald-800 border-emerald-200", Icon: CheckCircle2 },
  ignored:   { label: "Ignorada",       tone: "bg-muted text-muted-foreground border-border", Icon: EyeOff },
};

export default function BankReconciliation({ restaurantId, categories, catLabels, onChange }) {
  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [filter, setFilter] = useState("all");
  const [matchTx, setMatchTx] = useState(null);
  const [createExpTx, setCreateExpTx] = useState(null);
  const [confirmDel, setConfirmDel] = useState(null);
  const [rulesOpen, setRulesOpen] = useState(false);
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    if (!restaurantId) return;
    setLoading(true);
    try {
      const [list, s] = await Promise.all([
        api.listBankTransactions(restaurantId),
        api.getBankTransactionStats(restaurantId),
      ]);
      setRows(list || []);
      setStats(s);
    } catch {/* silent */}
    finally { setLoading(false); }
  }, [restaurantId]);

  useEffect(() => { load(); }, [load]);

  const handleImport = async (file) => {
    if (!file) return;
    setUploading(true);
    toast.info("Detectando columnas con IA…", { duration: 4000 });
    try {
      const res = await api.bankImportCSV(restaurantId, file);
      const autoRules = res.applied_rules || 0;
      toast.success(
        autoRules > 0
          ? `${res.total} transacciones · ${res.matched} conciliadas (${autoRules} por reglas aprendidas)`
          : `${res.total} transacciones importadas · ${res.matched} auto-conciliadas`,
        { duration: 5000 },
      );
      await load();
      onChange?.();
    } catch (err) {
      toast.error(err.message || "Error al importar CSV");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const handleIgnore = async (tx) => {
    try {
      await api.ignoreBankTransaction(restaurantId, tx.transaction_id);
      toast.success("Transacción ignorada");
      await load();
    } catch (err) { toast.error(err.message || "Error"); }
  };

  const handleUnmatch = async (tx) => {
    try {
      await api.unmatchBankTransaction(restaurantId, tx.transaction_id);
      toast.success("Emparejamiento deshecho");
      await load();
      onChange?.();
    } catch (err) { toast.error(err.message || "Error"); }
  };

  const handleDelete = async (tx) => {
    try {
      await api.deleteBankTransaction(restaurantId, tx.transaction_id);
      toast.success("Transacción eliminada");
      setConfirmDel(null);
      await load();
      onChange?.();
    } catch (err) { toast.error(err.message || "Error"); }
  };

  const filtered = filter === "all" ? rows : rows.filter(r => r.status === filter);

  return (
    <div className="space-y-5" data-testid="bank-reconciliation">
      {/* Dropzone */}
      <div
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => { e.preventDefault(); const f = e.dataTransfer.files?.[0]; if (f) handleImport(f); }}
        className="rounded-2xl border-2 border-dashed border-indigo-300 bg-indigo-50/50 p-6 text-center"
        data-testid="bank-dropzone"
      >
        <Landmark className="h-8 w-8 mx-auto text-indigo-600 mb-2" />
        <p className="text-sm text-muted-foreground mb-3">
          Arrastra el extracto bancario (CSV) — la IA detectará las columnas y conciliará con tus gastos automáticamente.
        </p>
        <input
          ref={fileRef}
          type="file"
          accept=".csv,text/csv,application/vnd.ms-excel"
          onChange={(e) => handleImport(e.target.files?.[0])}
          className="hidden"
          data-testid="bank-csv-input"
        />
        <Button onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="bank-upload-btn">
          {uploading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Upload className="h-4 w-4 mr-2" />}
          {uploading ? "Analizando con IA…" : "Importar extracto"}
        </Button>
        <Button
          variant="ghost" size="sm" onClick={() => setRulesOpen(true)}
          className="ml-2 text-violet-700 hover:bg-violet-50"
          data-testid="bank-rules-open-btn"
        >
          <Brain className="h-4 w-4 mr-1.5" /> Reglas aprendidas
        </Button>
      </div>

      {/* Stats summary */}
      {stats && (stats.matched + stats.unmatched + stats.ignored) > 0 && (
        <div className="grid grid-cols-3 gap-3">
          <StatCard count={stats.matched} total={stats.matched_total} label="Conciliadas" tone="emerald" onClick={() => setFilter("matched")} active={filter === "matched"} />
          <StatCard count={stats.unmatched} total={stats.unmatched_total} label="Sin emparejar" tone="amber" onClick={() => setFilter("unmatched")} active={filter === "unmatched"} />
          <StatCard count={stats.ignored} total={stats.ignored_total} label="Ignoradas" tone="gray" onClick={() => setFilter("ignored")} active={filter === "ignored"} />
        </div>
      )}

      {/* Filter & toggle */}
      {rows.length > 0 && (
        <div className="flex items-center justify-between">
          <div className="flex gap-1">
            {["all", "unmatched", "matched", "ignored"].map(v => (
              <Button
                key={v} size="sm" variant={filter === v ? "default" : "ghost"}
                onClick={() => setFilter(v)}
                data-testid={`bank-filter-${v}`}
              >
                {v === "all" ? "Todas" : STATUS_META[v].label}
              </Button>
            ))}
          </div>
          <div className="text-xs text-muted-foreground">
            {filtered.length} / {rows.length} transacción{rows.length === 1 ? "" : "es"}
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
      ) : filtered.length === 0 ? (
        <EmptyState hasRows={rows.length > 0} filter={filter} />
      ) : (
        <div className="space-y-2">
          {filtered.map(tx => (
            <TxRow
              key={tx.transaction_id}
              tx={tx}
              onMatch={() => setMatchTx(tx)}
              onIgnore={() => handleIgnore(tx)}
              onUnmatch={() => handleUnmatch(tx)}
              onCreateExpense={() => setCreateExpTx(tx)}
              onDelete={() => setConfirmDel(tx)}
            />
          ))}
        </div>
      )}

      {matchTx && (
        <BankMatchDialog
          tx={matchTx}
          restaurantId={restaurantId}
          open={!!matchTx}
          onClose={() => setMatchTx(null)}
          onDone={async () => { setMatchTx(null); await load(); onChange?.(); }}
        />
      )}

      {createExpTx && (
        <BankCreateExpenseDialog
          tx={createExpTx}
          restaurantId={restaurantId}
          categories={categories}
          open={!!createExpTx}
          onClose={() => setCreateExpTx(null)}
          onDone={async () => { setCreateExpTx(null); await load(); onChange?.(); }}
        />
      )}

      {confirmDel && (
        <AlertDialog open={!!confirmDel} onOpenChange={() => setConfirmDel(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>¿Eliminar transacción?</AlertDialogTitle>
              <AlertDialogDescription>
                {confirmDel.description} · {fmtEUR(confirmDel.amount)}
                {confirmDel.status === "matched" && <><br /><strong>Nota:</strong> el gasto asociado volverá a estado "sin pagar".</>}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancelar</AlertDialogCancel>
              <AlertDialogAction className="bg-red-600 hover:bg-red-700" onClick={(e) => { e.preventDefault(); handleDelete(confirmDel); }}>
                Eliminar
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}

      <BankRulesDrawer
        restaurantId={restaurantId}
        categories={categories}
        catLabels={catLabels}
        open={rulesOpen}
        onClose={() => setRulesOpen(false)}
        onChange={load}
      />
    </div>
  );
}

// ─── Small components ────────────────────────────────────────────────

function StatCard({ count, total, label, tone, onClick, active }) {
  const tones = {
    emerald: "from-emerald-50 to-emerald-100 border-emerald-200 text-emerald-900",
    amber:   "from-amber-50 to-amber-100 border-amber-200 text-amber-900",
    gray:    "from-gray-50 to-gray-100 border-border text-foreground",
  };
  return (
    <button
      onClick={onClick}
      className={`rounded-2xl border bg-gradient-to-br ${tones[tone]} p-3 text-left transition ${active ? "ring-2 ring-offset-1 ring-current/40 shadow-md" : "hover:shadow-sm"}`}
      data-testid={`bank-stat-${label.toLowerCase().replace(/\s/g, "-")}`}
    >
      <div className="text-2xl font-bold tracking-tight">{count}</div>
      <div className="text-[11px] opacity-80">{label}</div>
      <div className="text-[10px] opacity-60 mt-0.5">{fmtEUR(total)}</div>
    </button>
  );
}

function EmptyState({ hasRows, filter }) {
  const labels = {
    all: "Sin transacciones. Importa tu primer extracto bancario.",
    unmatched: "Todas las transacciones están conciliadas o ignoradas.",
    matched: "Aún no has conciliado ninguna transacción.",
    ignored: "No has ignorado ninguna transacción.",
  };
  return (
    <Card className="border-dashed">
      <CardContent className="py-10 text-center text-sm text-muted-foreground flex flex-col items-center gap-2">
        <FileSpreadsheet className="h-6 w-6 opacity-40" />
        {hasRows ? labels[filter] : labels.all}
      </CardContent>
    </Card>
  );
}

function TxRow({ tx, onMatch, onIgnore, onUnmatch, onCreateExpense, onDelete }) {
  const meta = STATUS_META[tx.status] || STATUS_META.unmatched;
  const { Icon } = meta;
  const isExpense = tx.amount < 0;
  return (
    <Card data-testid={`bank-tx-row-${tx.transaction_id}`}>
      <CardContent className="p-3 flex items-center gap-3">
        <div className={`shrink-0 h-10 w-10 rounded-lg flex items-center justify-center ${isExpense ? "bg-red-50 text-red-700" : "bg-emerald-50 text-emerald-700"}`}>
          <Landmark className="h-5 w-5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-medium text-foreground truncate">{tx.description}</span>
            <span className={`inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded border ${meta.tone}`}>
              <Icon className="h-3 w-3" /> {meta.label}
            </span>
            {tx.matched_score != null && tx.matched_score < 1 && (
              <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-violet-50 text-violet-700 border border-violet-200 inline-flex items-center gap-1">
                <Sparkles className="h-3 w-3" /> {Math.round(tx.matched_score * 100)}%
              </span>
            )}
            {tx.applied_rule_id && (
              <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 border border-emerald-200 inline-flex items-center gap-1" title="Creado automáticamente por una regla aprendida">
                <Zap className="h-3 w-3" /> Regla
              </span>
            )}
          </div>
          <div className="text-xs text-muted-foreground mt-0.5">
            {fmtDate(tx.date)}{tx.balance != null && <> · saldo {fmtEUR(tx.balance)}</>}
          </div>
        </div>
        <div className={`text-sm font-semibold shrink-0 ${isExpense ? "text-red-700" : "text-emerald-700"}`}>
          {fmtEUR(tx.amount)}
        </div>

        <div className="flex items-center gap-1 shrink-0">
          {tx.status === "unmatched" && (
            <>
              <Button size="sm" variant="outline" onClick={onMatch} data-testid={`bank-match-btn-${tx.transaction_id}`}>
                <Link2 className="h-3.5 w-3.5 mr-1" /> Emparejar
              </Button>
              {isExpense && (
                <Button size="sm" variant="ghost" onClick={onCreateExpense} data-testid={`bank-create-expense-btn-${tx.transaction_id}`}>
                  <Plus className="h-3.5 w-3.5 mr-1" /> Crear gasto
                </Button>
              )}
              <Button size="sm" variant="ghost" onClick={onIgnore} title="Ignorar">
                <EyeOff className="h-3.5 w-3.5" />
              </Button>
            </>
          )}
          {tx.status === "matched" && (
            <Button size="sm" variant="ghost" onClick={onUnmatch} title="Deshacer emparejamiento" data-testid={`bank-unmatch-btn-${tx.transaction_id}`}>
              <Link2Off className="h-3.5 w-3.5 mr-1" /> Deshacer
            </Button>
          )}
          <button
            onClick={onDelete}
            className="text-muted-foreground hover:text-red-700 p-1"
            aria-label="Eliminar"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        </div>
      </CardContent>
    </Card>
  );
}

function BankMatchDialog({ tx, restaurantId, open, onClose, onDone }) {
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [linking, setLinking] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const c = await api.getBankTransactionCandidates(restaurantId, tx.transaction_id);
        if (!cancelled) setCandidates(c || []);
      } finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [restaurantId, tx.transaction_id]);

  const link = async (cand) => {
    setLinking(cand.id);
    try {
      await api.matchBankTransaction(restaurantId, tx.transaction_id, cand.type, cand.id);
      toast.success("Transacción emparejada");
      onDone?.();
    } catch (err) { toast.error(err.message || "Error al emparejar"); }
    finally { setLinking(null); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Link2 className="h-5 w-5 text-indigo-600" /> Emparejar transacción
          </DialogTitle>
          <DialogDescription>
            {tx.description} · {fmtDate(tx.date)} · <strong>{fmtEUR(tx.amount)}</strong>
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="py-8 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
        ) : candidates.length === 0 ? (
          <div className="py-8 text-center text-sm text-muted-foreground">
            No encontramos gastos ni facturas pendientes que se parezcan a esta transacción.
            <br />Puedes crear un gasto nuevo directamente.
          </div>
        ) : (
          <div className="space-y-2" data-testid="bank-match-candidates">
            {candidates.map(c => (
              <Card key={`${c.type}-${c.id}`} className="hover:shadow-sm transition">
                <CardContent className="p-3 flex items-center gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-medium truncate">{c.label || "—"}</span>
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-muted border border-border text-foreground">
                        {c.type === "expense" ? "Gasto" : "Factura"}
                      </span>
                      {c.score > 0 && (
                        <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded border inline-flex items-center gap-1 ${c.score >= 0.7 ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-amber-50 text-amber-800 border-amber-200"}`}>
                          <Sparkles className="h-3 w-3" /> {Math.round(c.score * 100)}%
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-muted-foreground mt-0.5">
                      {fmtDate(c.date)} · {fmtEUR(c.amount)}
                    </div>
                  </div>
                  <Button
                    size="sm"
                    onClick={() => link(c)}
                    disabled={linking === c.id}
                    data-testid={`bank-match-pick-${c.id}`}
                  >
                    {linking === c.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <>Vincular <ArrowRight className="h-3.5 w-3.5 ml-1" /></>}
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function BankCreateExpenseDialog({ tx, restaurantId, categories, open, onClose, onDone }) {
  const [category, setCategory] = useState("other");
  const [supplier, setSupplier] = useState(tx.description.slice(0, 80));
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [aiLoading, setAiLoading] = useState(true);
  const [aiResult, setAiResult] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.aiClassifyBankTransaction(restaurantId, tx.transaction_id);
        if (cancelled) return;
        if (res && res.category) {
          setCategory(res.category);
          if (res.supplier_name) setSupplier(res.supplier_name);
          setAiResult(res);
        }
      } catch {/* silent — user can still fill manually */}
      finally { if (!cancelled) setAiLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [restaurantId, tx.transaction_id]);

  const save = async () => {
    setSaving(true);
    try {
      await api.createExpenseFromBank(restaurantId, tx.transaction_id, {
        category, supplier_name: supplier, notes: notes || null,
      });
      toast.success("Gasto creado y conciliado");
      onDone?.();
    } catch (err) { toast.error(err.message || "Error al crear"); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Crear gasto desde transacción</DialogTitle>
          <DialogDescription>
            {tx.description} · {fmtDate(tx.date)} · <strong>{fmtEUR(tx.amount)}</strong>
          </DialogDescription>
        </DialogHeader>
        {aiLoading && (
          <div className="text-xs flex items-center gap-2 rounded-lg border border-violet-200 bg-violet-50 text-violet-800 px-3 py-2">
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
            Sugiriendo con IA…
          </div>
        )}
        {!aiLoading && aiResult && (
          <div className={`text-xs flex items-center gap-2 rounded-lg border px-3 py-2 ${aiResult.confidence >= 0.8 ? "border-emerald-200 bg-emerald-50 text-emerald-800" : "border-amber-200 bg-amber-50 text-amber-800"}`}>
            <Sparkles className="h-3.5 w-3.5" />
            IA sugiere <strong>{aiResult.supplier_name || "—"}</strong> · categoría <strong>{catLabelOf(categories, aiResult.category)}</strong>
            <span className="ml-auto opacity-70">
              {Math.round((aiResult.confidence || 0) * 100)}%
              {aiResult._source === "catalog" ? " · catálogo" : aiResult._cached ? " · caché" : ""}
            </span>
          </div>
        )}
        <div className="space-y-3">
          <div className="space-y-2">
            <Label>Proveedor / Concepto</Label>
            <Input value={supplier} onChange={(e) => setSupplier(e.target.value)} data-testid="bank-expense-supplier" />
          </div>
          <div className="space-y-2">
            <Label>Categoría</Label>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger data-testid="bank-expense-category"><SelectValue /></SelectTrigger>
              <SelectContent>
                {(categories || []).map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Notas</Label>
            <Input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Opcional" />
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={onClose}>Cancelar</Button>
            <Button onClick={save} disabled={saving} data-testid="bank-expense-save-btn">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Crear y conciliar"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function catLabelOf(categories, value) {
  return (categories || []).find(c => c.value === value)?.label || value;
}
